import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Row, ModalBody } from 'reactstrap';
import styled, { css, withTheme } from 'styled-components';
import { ifProp, prop } from 'styled-tools';
import { Helmet } from 'react-helmet';
import 'react-day-picker/lib/style.css';
import moment from 'moment';
import { ApolloConsumer, Query } from 'react-apollo';
import gql from 'graphql-tag';
import _ from 'lodash';
import filter from 'lodash/filter';
import some from 'lodash/some';
import isEmpty from 'lodash/isEmpty';
import omitBy from 'lodash/omitBy';
import values from 'lodash/values';
import memoize from 'fast-memoize';
import queryString from 'query-string';
import LargestCol from 'components/LargestCol';
import CalendarCol from './styled/CalendarCol';
import RewardsCol from './styled/RewardsCol';
import Calendar from './Calendar';
import PropertySelect from './PropertySelect';
import BookingDatesForm from './BookingDatesForm';
import ErrorModal from 'components/ErrorModal';
import WindcreekModal from 'components/WindcreekModal';
import Button from 'components/Button';
import GuestDetailsContainer from 'containers/GuestDetailsContainer';
import OffersFormContainer from '../containers/OffersFormContainer';
import BookYourStayContainer from 'containers/BookYourStayContainer';
import HostBookingDetailsContainer from 'containers/HostBookingDetailsContainer';
import OffersContainer from '../containers/OffersContainer';
import { gaEvent, gaSetPropertyDimension } from 'utils/googleAnalytics';
import { blockShape, submittedAccountShape } from 'utils/shapes';
import {
  ALL,
  MAX_WEB_OFFERS_IN_STAY,
  PREMIUM_ROOM_TYPES,
  PROPERTY_WETUMPKA,
  ROOM_TYPE_CODES_FOR_SIZE,
  SUNDAY,
  THURSDAY,
  MAX_DISCOUNTED_OR_COMP_NIGHTS
} from 'utils/constants';
import {
  getBookingEnginePath,
  getBookingSource,
  isHostBookingSource
} from 'utils/routeUtils';
import fragments from 'utils/graphql/fragments';
import { isWebOfferPrizeCode, WCH_HOTEL } from 'utils/rewards';
import { isMobileUserAgent } from 'utils/mobile';
import { roomRateIsComp } from 'utils/roomRates';

const QUERY_GET_RATES_AND_OFFERS = gql`
  query GetHotelCalendar($input: GetHotelCalendar!) {
    hotelCalendar(input: $input) {
      days {
        ...CalendarDayFields
      }
      rewards {
        ...HotelOfferFields
      }
      profile {
        ...HotelProfileFields
      }
      secondProfile {
        ...HotelProfileFields
      }
      error
      patronAccountNumber
      isAdmin
    }
  }
  ${fragments.HotelOfferFields}
  ${fragments.CalendarDayFields}
  ${fragments.HotelProfileFields}
`;

const QUERY_GET_HOTEL_OVERRIDE_CODES = gql`
  query GetHotelOverrideCodes {
    getHotelOverrideCodes {
      id
      hotelCode
      startDate
      endDate
      lastModified
    }
  }
`;

const StyledLargestCol = styled(LargestCol)`
  ${ifProp(
    'isHostBooking',
    css`
      @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
        padding: 0;
      }
    `
  )}
`;

const OffersAndGroupCodeContainer = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
`;

export class DateSelection extends Component {
  constructor(props) {
    super(props);

    this.state = {
      errors: [],
      roomType: props.roomType
    };
  }

  componentDidMount() {
    const {
      location,
      setToken,
      token,
      setBookingSource,
      setAccountNumber,
      submitRewardsAccount,
      setInMobileApp
    } = this.props;

    const params = queryString.parse(location.search);
    const tokenFromUrl = params.token;
    const bookingSourceFromUrl = params.source;
    const inMobileApp = params.mobileapp;

    if (bookingSourceFromUrl) {
      setBookingSource(getBookingSource(location));
    }

    if (tokenFromUrl && !token) {
      setToken(tokenFromUrl);
    }

    if (params.patronAccountNumber) {
      setAccountNumber(params.patronAccountNumber);
      submitRewardsAccount();
    }

    if (inMobileApp === 'true') {
      setInMobileApp(inMobileApp);
    }
  }

  handleSearchClick = selectedOffers => {
    const {
      startDate,
      endDate,
      history: { push },
      setSelectedOffers
    } = this.props;

    if (!startDate || !endDate) {
      this.setState({
        errors: ['From and To dates are required.'],
        errorPopoverOpen: true
      });
    } else {
      const offers = _.map(selectedOffers, (selectedOffer, day) =>
        selectedOffer
          ? { day, promotionCode: selectedOffer.patronReward.prizeCode }
          : null
      ).filter(selectedOffer => selectedOffer !== null);
      setSelectedOffers(offers);
      push(getBookingEnginePath('choose-accommodation'));
    }
  };

  onRoomTypeChange = roomType => {
    this.setState({ roomType });
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { startDate, endDate, setEndDate } = nextProps;

    if (
      startDate &&
      endDate &&
      moment(endDate)
        .startOf('day')
        .isSameOrBefore(startDate)
    ) {
      setEndDate(null);
    }

    if (!prevState.roomType && nextProps.roomType) {
      return {
        roomType: nextProps.roomType
      };
    } else {
      return null;
    }
  }

  handleRewardsAccountSubmit(
    refetch,
    accountNumber,
    accountPin,
    secondAccountNumber
  ) {
    const {
      setStartDate,
      setEndDate,
      submitRewardsAccount,
      submittedAccount,
      secondSubmittedAccount
    } = this.props;

    // Note that we also clear out the selected start and end dates here as well. This is on purpose, to
    // force the user to reselect dates now that their special rates/offers are loaded.
    setStartDate(null);
    setEndDate(null);

    // Submit the rewards account information to be used in the calendar query and rest of booking.
    submitRewardsAccount();

    // Manually refetch calendar data if the props haven't changed.
    if (
      accountNumber === submittedAccount.accountNumber &&
      accountPin === submittedAccount.accountPin &&
      secondAccountNumber === secondSubmittedAccount.accountNumber
    ) {
      return refetch();
    }

    gaEvent({
      category: 'Hotel Booking',
      action: 'Submitted rewards account to find rates',
      label: accountNumber
    });
  }

  // Handles the date change for the calendar
  onDatesChange = ([startDate, endDate]) => {
    const { onStartDateChange, onEndDateChange } = this.props;

    const startMoment = startDate && moment(startDate);
    const endMoment = startDate && moment(endDate);
    const flipDates =
      startMoment && endMoment && endMoment.isBefore(startMoment);

    // If the endDate is before the startDate, then we should flip them.
    onStartDateChange(flipDates ? endDate : startDate);
    onEndDateChange(flipDates ? startDate : endDate);
  };

  onClickDay = value => {
    const {
      startDate,
      endDate,
      onStartDateChange,
      onEndDateChange
    } = this.props;

    // Both dates were selected, so user is clearing their selection by clicking this new day.
    if (startDate && endDate) {
      onStartDateChange(value);
      onEndDateChange(undefined);
    }
  };

  clearDates = () => {
    const { onStartDateChange, onEndDateChange } = this.props;

    onStartDateChange(null);
    onEndDateChange(null);
  };

  closeErrorPopover = e => {
    this.setState({
      errorPopoverOpen: false
    });
  };

  handlePropertyChange = e => {
    const { setProperty } = this.props;

    gaSetPropertyDimension(e.value.toLowerCase());
    gaEvent({
      category: 'Hotel Booking',
      action: 'Changed Property',
      label: e.value
    });

    setProperty(e.value);

    /* set room type to ALL */
    this.onRoomTypeChange(this.props.roomType);
  };

  rateIsForSelectedRoomType = rate => {
    const { roomType } = this.state;

    return (
      roomType.value === ALL ||
      ROOM_TYPE_CODES_FOR_SIZE[roomType.value].includes(rate.roomTypeCode)
    );
  };

  // bestRoomType is determined elsewhere. Here we find a room rate that
  // matches bestRoomType and is eligible for COMP.
  findCompRateForDayAndRoomType = (day, roomType, bestRoomType) => {
    return day && roomType
      ? _.find(
          day.roomRates,
          rate =>
            rate.roomTypeCode === bestRoomType &&
            this.rateIsForSelectedRoomType(rate) &&
            rate.eligibleForComp
        )
      : null;
  };

  // Returns true if the offerCode exists in the override codes, and the current date
  // falls within the date range of the override.
  //
  // offerCode is just the code itself (string)
  // curDate is a moment
  isOverrideCode = (offerCode, curDate) => {
    const { hotelOverrideCodes } = this.props;

    return some(hotelOverrideCodes, overrideCode => {
      const overrideStartDate = moment(overrideCode.startDate).startOf('day');
      const overrideEndDate = moment(overrideCode.endDate).startOf('day');

      return (
        offerCode === overrideCode.hotelCode &&
        curDate.isBetween(overrideStartDate, overrideEndDate, undefined, '[]')
      );
    });
  };

  getOfferDetails = (
    startDate,
    endDate,
    roomType,
    days,
    bestRoomType,
    adminSelectedOffers,
    isHostBooking
  ) => {
    if (!startDate || !endDate) {
      return {};
    }

    const start = moment(startDate);
    const end = moment(endDate);

    // Loop through days to find the room types that are available across full stay.
    const offerDetails = {};
    let curDate = start.clone();
    let includeWchHotel = false;

    while (curDate.isBefore(end, 'day')) {
      const formattedDate = curDate.format('YYYY-MM-DD');
      const day = _.find(days, { date: formattedDate });
      const dayOffers =
        day && day.roomRates.length > 0 ? day.roomRates[0].offers || [] : [];
      const rateEligibleForComp = this.findCompRateForDayAndRoomType(
        day,
        roomType,
        bestRoomType
      );
      const isCompable = !!rateEligibleForComp;

      // Find the intersection of this day's offers with the host's selected offers.
      // If adminSelectedOffers is null and this is host booking, we won't get any
      // results with the intersection, so we can use auto selected offers
      // as the initial state for selected offers.
      const filteredDayOffers =
        isHostBooking && adminSelectedOffers !== null
          ? _.intersectionWith(
              dayOffers,
              adminSelectedOffers,
              (a, b) => a.patronReward.prizeCode === b.patronReward.prizeCode
            )
          : dayOffers;

      // This flag tracks if this night is covered by an offer that is NOT WCHHOTEL.
      // It should only be true for nights that are covered, or aren't compable.
      // If this is false after looping over filteredDayOffers, it should indicate
      // that this night is compable and is NOT covered by a non-WCHHOTEL offer,
      // so WCHHOTEL can be used.
      let nightCovered = false;

      filteredDayOffers.forEach(dayOffer => {
        const prizeCode = dayOffer.patronReward.prizeCode;

        // check if already processed. If not, add base.
        if (!offerDetails[prizeCode]) {
          offerDetails[prizeCode] = {
            numberOfNightsCovered: 0,
            numberOfNights:
              dayOffer.patronReward.maxPrizesPerPeriod -
              dayOffer.patronReward.redeemed,
            isOverrideCode: false
          };
        }

        // if there are nights left in offer, increment nightsCovered.
        const hasNights =
          offerDetails[prizeCode].numberOfNights >
          offerDetails[prizeCode].numberOfNightsCovered;

        // Web offers (those starting with @ or C!H) only apply Sun - Thurs, and cannot be used on premium rooms.
        const isSuite = PREMIUM_ROOM_TYPES.includes(bestRoomType);
        const webOfferApplies =
          curDate.day() >= SUNDAY && curDate.day() <= THURSDAY && !isSuite;
        const isWebOffer = isWebOfferPrizeCode(prizeCode);
        const isOverrideCode = this.isOverrideCode(prizeCode, curDate);

        if (
          hasNights &&
          (isCompable ||
            (isWebOffer && webOfferApplies) ||
            (isOverrideCode && !isSuite))
        ) {
          offerDetails[prizeCode].numberOfNightsCovered++;

          // Override code date range could cover only part of the stay.
          // We want isOverrideCode to be true if any day in the stay is covered.
          if (!offerDetails[prizeCode].isOverrideCode) {
            offerDetails[prizeCode].isOverrideCode = isOverrideCode;
          }

          // Only mark this night as covered if this offer is NOT WCHHOTEL.
          // We will use nightCovered later to determine if WCHHOTEL can be used.
          if (prizeCode !== WCH_HOTEL) {
            nightCovered = true;
          }
        } else {
          // If not compable, mark the night as "covered" so we don't return WCHHOTEL
          if (!isCompable) {
            nightCovered = true;
          }
        }
      });

      // If this is true, this night cannot be covered
      if (!nightCovered) {
        includeWchHotel = true;
      }

      curDate.add(1, 'd');
    }

    // Only apply WCHHOTEL if there is a day in the stay which cannot be
    // covered by another offer.
    if (!includeWchHotel) {
      return omitBy(offerDetails, (value, key) => key === WCH_HOTEL);
    } else {
      return offerDetails;
    }
  };

  getDaysInStay = (startDate, endDate, days, bestRoomType, property) => {
    const daysInStay = [];
    const end = moment(endDate);

    let curDate = moment(startDate);
    while (curDate.isBefore(end, 'day')) {
      const formattedDate = curDate.format('YYYY-MM-DD');
      const day = _.find(days, { date: formattedDate });

      const minRate = day
        ? _.minBy(
            day.roomRates.filter(
              roomRate =>
                roomRate.roomTypeCode === bestRoomType &&
                this.rateIsForSelectedRoomType(roomRate) &&
                roomRate.rates.length > 0 &&
                !roomRateIsComp(roomRate.rates[0].base.amount, property)
            ),
            roomRate =>
              roomRate.rates.length > 0 && roomRate.rates[0].base.amount
          )
        : null;

      const minPaidRate =
        minRate && minRate.rates.length > 0
          ? minRate.rates[0].base.amount
          : null;

      daysInStay.push({
        date: formattedDate,
        calendarDay: day,
        minPaidRate
      });

      curDate.add(1, 'd');
    }

    return daysInStay;
  };

  getAvailableRoomTypesForStay = memoize(
    (startDate, endDate, roomType, days) => {
      if (!startDate || !endDate) {
        return [];
      }

      const end = moment(endDate);

      // Loop through days to find the room types that are available across full stay.
      const availableRoomTypesPerDay = [];
      let curDate = moment(startDate);
      while (curDate.isBefore(end, 'day')) {
        const formattedDate = curDate.format('YYYY-MM-DD');
        const day = _.find(days, { date: formattedDate });

        if (day) {
          availableRoomTypesPerDay.push(
            day.roomRates.map(roomRate => roomRate.roomTypeCode)
          );
        }

        curDate.add(1, 'd');
      }

      const roomTypesForStay = _.intersection(...availableRoomTypesPerDay);

      return roomType.value !== ALL
        ? roomTypesForStay.filter(type =>
            ROOM_TYPE_CODES_FOR_SIZE[roomType.value].includes(type)
          )
        : roomTypesForStay;
    }
  );

  calculateBestRoomTypeForStay = memoize(
    (startDate, endDate, roomType, days) => {
      if (!startDate || !endDate) {
        return null;
      }

      const end = moment(endDate);

      // Loop through days to find the room types that are available across full stay.
      const daysMap = {};
      let curDate = moment(startDate);
      while (curDate.isBefore(end, 'day')) {
        const formattedDate = curDate.format('YYYY-MM-DD');
        daysMap[formattedDate] = _.find(days, { date: formattedDate });

        curDate.add(1, 'd');
      }

      const availableRoomTypes = this.getAvailableRoomTypesForStay(
        startDate,
        endDate,
        roomType,
        days
      );

      const roomTypeTotals = availableRoomTypes.map(type => ({
        roomType: type,
        total: _.reduce(
          daysMap,
          (sum, day) => {
            const minRate = day
              ? _.minBy(
                  day.roomRates.filter(
                    roomRate =>
                      roomRate.roomTypeCode === type &&
                      roomRate.rates.length > 0
                  ),
                  roomRate =>
                    roomRate.rates.length > 0 && roomRate.rates[0].base.amount
                )
              : null;

            const minPaidRate =
              minRate && minRate.rates.length > 0
                ? minRate.rates[0].base.amount
                : 0;

            return sum + minPaidRate;
          },
          0
        )
      }));

      const bestRoomType = _.minBy(roomTypeTotals, 'total');

      return bestRoomType ? bestRoomType.roomType : null;
    }
  );

  dayIsCompable = (day, bestRoomType, property) => {
    return (
      day &&
      day.roomRates.find(
        roomRate =>
          roomRate.roomTypeCode === bestRoomType &&
          this.rateIsForSelectedRoomType(roomRate) &&
          roomRate.rates.length > 0 &&
          roomRateIsComp(roomRate.rates[0].base.amount, property)
      )
    );
  };

  sortOffers = offers => {
    return _.orderBy(
      offers,
      [
        offer => offer.details.numberOfNightsCovered,
        offer =>
          offer.details.numberOfNights - offer.details.numberOfNightsCovered,
        offer => offer.offer.bookingEndDate
      ],
      ['desc', 'asc', 'asc']
    );
  };

  getSelectedOffers = (
    sortedDaysInStay,
    bestRoomType,
    offerDetails,
    property
  ) => {
    const selectedMailerOffers = {};
    const selectedWebOffers = {};
    let nightsCoveredWithComp = 0;

    if (isEmpty(offerDetails)) {
      return [[], []];
    }

    // One of the offers in offerDetails matches an override code, which
    // can be used without comp rates.
    const hasOverrideCodes = values(offerDetails).some(
      offerDetail => offerDetail.isOverrideCode
    );

    sortedDaysInStay.forEach(dayInStay => {
      const formattedDate = dayInStay.date;
      const day = dayInStay.calendarDay;
      const dayMoment = moment(formattedDate);
      const isSuite = PREMIUM_ROOM_TYPES.includes(bestRoomType);
      const webOfferApplies =
        !isSuite && dayMoment.day() >= SUNDAY && dayMoment.day() <= THURSDAY;

      // Find out if this day and room type has a comp rate.
      const isCompable = !!this.dayIsCompable(day, bestRoomType, property);

      const dayOffers =
        day && day.roomRates.length > 0 ? day.roomRates[0].offers || [] : [];

      const dayOffersDetail = dayOffers
        .map(dayOffer => ({
          offer: dayOffer,
          details: offerDetails[dayOffer.patronReward.prizeCode] || null
        }))
        .filter(offer => (offer?.details?.nightsRemaining || 0) > 0);

      const mailerOffersDetail = dayOffersDetail.filter(
        dayOffer => !isWebOfferPrizeCode(dayOffer.offer.patronReward.prizeCode)
      );
      const webOffersDetail = dayOffersDetail.filter(dayOffer =>
        isWebOfferPrizeCode(dayOffer.offer.patronReward.prizeCode)
      );

      // Sort offers first by number of nights covered (descending),
      // then by number of nights remaining (ascending),
      // then by offer end date (ascending).
      //
      // As nightsRemaining changes, this sort order will change
      // for each day in the stay.
      const sortedMailerOffers = this.sortOffers(mailerOffersDetail);
      const sortedWebOffers = this.sortOffers(webOffersDetail);

      let selectedMailerOffer = null;
      let selectedWebOffer = null;

      if (isCompable && sortedMailerOffers.length > 0) {
        if (nightsCoveredWithComp < MAX_DISCOUNTED_OR_COMP_NIGHTS) {
          selectedMailerOffer = sortedMailerOffers[0];
          selectedMailerOffer.details.nightsRemaining--;
          nightsCoveredWithComp++;
        }
      } else if (!isCompable && hasOverrideCodes) {
        // find override codes that apply to this day
        const overrideCodes = sortedMailerOffers.filter(
          offer =>
            offer.details.isOverrideCode &&
            this.isOverrideCode(offer.offer.patronReward.prizeCode, dayMoment)
        );

        if (overrideCodes.length > 0) {
          selectedMailerOffer = sortedMailerOffers[0];
          selectedMailerOffer.details.nightsRemaining--;
          nightsCoveredWithComp++;
        }
      }

      // If web offer available and allowed on date, and we haven't reached
      // max web offers on stay, add to selected web offers.
      if (
        webOfferApplies &&
        sortedWebOffers.length > 0 &&
        _.size(selectedWebOffers) < MAX_WEB_OFFERS_IN_STAY
      ) {
        if (nightsCoveredWithComp < MAX_DISCOUNTED_OR_COMP_NIGHTS) {
          selectedWebOffer = sortedWebOffers[0];
          selectedWebOffer.details.nightsRemaining--;
          nightsCoveredWithComp++;
        }
      }

      if (selectedMailerOffer) {
        selectedMailerOffers[formattedDate] = selectedMailerOffer.offer;
      }

      if (selectedWebOffer) {
        selectedWebOffers[formattedDate] = selectedWebOffer.offer;
      }
    });

    return [selectedMailerOffers, selectedWebOffers];
  };

  calculateSelectedOffers = memoize(
    (
      startDate,
      endDate,
      roomType,
      days,
      bestRoomType,
      adminSelectedOffers,
      isHostBooking,
      property
    ) => {
      if (!startDate || !endDate) {
        return {};
      }

      const offerDetails = this.getOfferDetails(
        startDate,
        endDate,
        roomType,
        days,
        bestRoomType,
        adminSelectedOffers,
        isHostBooking
      );

      // Initialize a field on each offer that will be used to keep track of
      // how many more nights the offer can be used as it gets
      // assigned to nights in the selected date range below.
      _.each(offerDetails, offer => {
        offer.nightsRemaining = offer.numberOfNightsCovered;
      });

      const daysInStay = this.getDaysInStay(
        startDate,
        endDate,
        days,
        bestRoomType,
        property
      );

      const sortedDaysInStay = _.orderBy(
        daysInStay,
        ['minPaidRate', 'date'],
        ['desc', 'asc']
      );

      const [selectedMailerOffers, selectedWebOffers] = this.getSelectedOffers(
        sortedDaysInStay,
        bestRoomType,
        offerDetails,
        property
      );

      // Determine whether we should use web offers or mailer offers on the stay,
      // since it is not allowed to use both on the same stay.
      // We choose the offer type that will cover the most number of days in the stay.
      return _.size(selectedWebOffers) >= _.size(selectedMailerOffers)
        ? selectedWebOffers
        : selectedMailerOffers;
    }
  );

  onQueryCompleted = data => {
    const {
      setAccountNumber,
      setAccountPin,
      submitRewardsAccount,
      setIsAdmin,
      setGuestProfiles
    } = this.props;

    const rateData = (data && data['hotelCalendar']) || {};
    const { patronAccountNumber, isAdmin, profile, secondProfile } =
      rateData || {};

    // In the event of an error, graphql service does not return the isAdmin flag,
    // so in this case, preserve its current value. Otherwise, changing the value here
    // can lead to an infinite loop of requests.
    if (!rateData.error) {
      setIsAdmin(isAdmin);
    }

    setGuestProfiles(
      filter([profile, secondProfile], guestProfile => !!guestProfile)
    );

    if (
      rateData.error &&
      (rateData.error.includes('Invalid account number') ||
        rateData.error.includes('is locked out') ||
        rateData.error.includes('banned'))
    ) {
      setAccountNumber(null);
      setAccountPin(null);
      submitRewardsAccount();
    } else if (patronAccountNumber) {
      !isAdmin && setAccountNumber(patronAccountNumber);
      setAccountPin(null);
      submitRewardsAccount();
    }
  };

  render() {
    const {
      startDate,
      endDate,
      property,
      setQueryInput,
      queryInput,
      blockCode,
      submittedBlockCode,
      location,
      isAdmin,
      theme,
      bookingSource,
      roomType,
      setActiveBlock,
      adminSelectedOffers,
      token,
      submittedAccount,
      onStartDateChange,
      onEndDateChange,
      setHotelOverrideCodes,
      hotelOverrideCodes,
      setMaxStayExceeded,
      maxStayExceeded,
      inMobileApp
    } = this.props;

    const isHostBooking = isHostBookingSource(bookingSource);

    const { errors, errorPopoverOpen } = this.state;

    // If user is logged in, we want to make sure we don't send account number / PIN in request.
    const params = queryString.parse(location.search);
    const isAuthenticated = !!params.token;

    const filteredQueryInput = _.cloneDeep(queryInput);
    if (submittedBlockCode || (!isAdmin && isAuthenticated)) {
      delete filteredQueryInput.patronAccountNumber;
      delete filteredQueryInput.pin;
    }

    if (submittedBlockCode) {
      filteredQueryInput.blockCode = submittedBlockCode;
    }

    if (isHostBooking) {
      filteredQueryInput.isHostBooking = true;
    }

    const isMobile = isMobileUserAgent();

    return (
      <React.Fragment>
        <Helmet>
          <title>Choose Dates</title>
        </Helmet>
        <WindcreekModal isOpen={maxStayExceeded} centered autoFocus>
          <ModalBody>
            <p>
              A hotel stay cannot be booked for more than 5 consecutive nights
              at a time. Please contact the call center for further assistance.
            </p>
            <Button onClick={() => setMaxStayExceeded(false)}>OKAY</Button>
          </ModalBody>
        </WindcreekModal>
        <ApolloConsumer>
          {client => {
            let previousMonth = null;

            client
              .query({
                query: QUERY_GET_HOTEL_OVERRIDE_CODES
              })
              .then(response => {
                setHotelOverrideCodes(response.data.getHotelOverrideCodes);
              })
              .catch(() => {
                console.log('No hotel override codes found.');
              });

            if (startDate && endDate) {
              const activeMoment = moment(queryInput.startDate);
              const startMoment = moment(startDate);
              const endMoment = moment(endDate);

              // If startDate is not in active month, or endDate not in active month, we load the other month's data from apollo cache.
              if (
                activeMoment.month() !== startMoment.month() ||
                activeMoment.month() !== endMoment.month()
              ) {
                const targetMoment =
                  activeMoment.month() !== startMoment.month()
                    ? startMoment
                    : endMoment;
                const targetMonthStartMoment = targetMoment
                  .clone()
                  .startOf('month');

                const targetMonthEndMoment = targetMonthStartMoment
                  .clone()
                  .endOf('month');

                const monthStart = moment().isBetween(
                  targetMonthStartMoment,
                  targetMonthEndMoment,
                  'day',
                  '[]'
                )
                  ? moment().format('YYYY-MM-DD')
                  : targetMonthStartMoment.format('YYYY-MM-DD');

                const monthEnd =
                  queryInput.property === PROPERTY_WETUMPKA
                    ? targetMonthEndMoment.add(1, 'days').format('YYYY-MM-DD')
                    : targetMonthEndMoment.format('YYYY-MM-DD');

                const additionalMonthInput = _.cloneDeep(filteredQueryInput);
                additionalMonthInput.startDate = monthStart;
                additionalMonthInput.endDate = monthEnd;

                try {
                  const cacheResult = client.readQuery({
                    query: QUERY_GET_RATES_AND_OFFERS,
                    variables: { input: additionalMonthInput }
                  });

                  previousMonth = cacheResult || null;
                } catch (e) {
                  // An exception is thrown if the data for the query does not exist in the cache.
                  // In this case, we catch the exception and make the request so that it will be available in a future render.
                  //
                  // This ideally shouldn't ever occur, however there are other issues with how the calendar and day pickers
                  // interact that is causing this to occur if user selects future dates from the day pickers. Using this as
                  // a temporarily workaround until we can address that issue.
                  client.query({
                    query: QUERY_GET_RATES_AND_OFFERS,
                    variables: { input: additionalMonthInput }
                  });
                }
              }
            }

            return (
              <Query
                query={QUERY_GET_RATES_AND_OFFERS}
                variables={{ input: filteredQueryInput }}
                skip={!queryInput}
                onCompleted={this.onQueryCompleted}
                notifyOnNetworkStatusChange
              >
                {({ loading, error, data, refetch }) => {
                  const rateData = (data && data['hotelCalendar']) || {};
                  const {
                    days,
                    rewards,
                    error: patronOrGroupCodeError,
                    patronAccountNumber,
                    isAdmin
                  } = rateData;

                  const calendarDays = days || rateData.calendarDays || [];

                  const combinedDays = _.sortBy(
                    [
                      ...(previousMonth?.hotelCalendar?.days || []),
                      ...calendarDays
                    ],
                    'date'
                  );

                  const bestRoomType = this.calculateBestRoomTypeForStay(
                    startDate,
                    endDate,
                    roomType,
                    combinedDays
                  );

                  const selectedOffers = this.calculateSelectedOffers(
                    startDate,
                    endDate,
                    roomType,
                    combinedDays,
                    bestRoomType,
                    adminSelectedOffers,
                    isHostBooking,
                    property
                  );

                  const showPropertySelect =
                    !theme.propertySelector.hidden &&
                    (!navigator.userAgent.includes('Mobi') ||
                      !theme.propertySelector.hiddenOnMobile);

                  return (
                    <React.Fragment>
                      {error && (
                        <ErrorModal
                          property={property}
                          isOpen={true}
                          error={error}
                        />
                      )}
                      <Row>
                        <CalendarCol md="12" lg={isHostBooking ? 5 : 7}>
                          {showPropertySelect && !inMobileApp && (
                            <PropertySelect
                              value={property}
                              onChange={this.handlePropertyChange}
                            />
                          )}
                          {inMobileApp && isHostBooking && (
                            <GuestDetailsContainer />
                          )}
                          {isMobile && isHostBooking && (
                            <>
                              {(!token || isAdmin) && (
                                <OffersFormContainer
                                  loading={
                                    loading && !!submittedAccount.accountNumber
                                  }
                                  disabled={!isAdmin && !!patronAccountNumber}
                                />
                              )}
                              <BookingDatesForm
                                startDate={startDate}
                                endDate={endDate}
                                onStartDateChange={onStartDateChange}
                                onEndDateChange={onEndDateChange}
                                queryInput={queryInput}
                                isHostBooking={isHostBooking}
                                isAdmin={isAdmin}
                              />
                            </>
                          )}
                          <Calendar
                            startDate={startDate}
                            endDate={endDate}
                            onDatesChange={this.onDatesChange}
                            onClickDay={this.onClickDay}
                            setQueryInput={setQueryInput}
                            queryInput={queryInput}
                            calendarDays={combinedDays}
                            loading={loading}
                            error={error}
                            offers={rewards}
                            selectedOffers={selectedOffers}
                            roomType={roomType}
                            property={property}
                            bestRoomType={bestRoomType}
                            isHostBooking={isHostBooking}
                            activeStartDate={moment(
                              queryInput.startDate
                            ).toDate()}
                            setActiveBlock={setActiveBlock}
                            blockCode={blockCode}
                            clearDates={this.clearDates}
                            hotelOverrideCodes={hotelOverrideCodes}
                            property={property}
                          />
                        </CalendarCol>
                        <RewardsCol
                          md="12"
                          lg={isHostBooking ? 4 : 5}
                          isHostBooking={isHostBooking}
                        >
                          <Row>
                            <LargestCol xs="12">
                              {showPropertySelect && (
                                <PropertySelect
                                  value={property}
                                  onChange={this.handlePropertyChange}
                                />
                              )}
                            </LargestCol>
                          </Row>
                          <Row>
                            <LargestCol
                              xs="12"
                              md={isHostBooking ? 12 : 7}
                              lg="12"
                              className="offers-form-container"
                            >
                              <OffersAndGroupCodeContainer>
                                {(!token || isAdmin) &&
                                  (!isHostBooking || !isMobile) && (
                                    <OffersFormContainer
                                      loading={
                                        loading &&
                                        !!submittedAccount.accountNumber
                                      }
                                      disabled={
                                        !isAdmin && !!patronAccountNumber
                                      }
                                    />
                                  )}
                                <OffersContainer
                                  authenticatedNonAdmin={
                                    !isAdmin && isAuthenticated
                                  }
                                  error={patronOrGroupCodeError}
                                  selectedOffers={selectedOffers}
                                  rewards={rewards}
                                />
                              </OffersAndGroupCodeContainer>
                            </LargestCol>
                            {!isHostBooking && (
                              <LargestCol xs="12" md="5" lg="12">
                                <BookYourStayContainer
                                  errors={errors}
                                  errorPopoverOpen={errorPopoverOpen}
                                  closeErrorPopover={this.closeErrorPopover}
                                  onNextClick={this.handleSearchClick.bind(
                                    this,
                                    selectedOffers
                                  )}
                                  isAdmin={isAdmin}
                                />
                              </LargestCol>
                            )}
                          </Row>
                        </RewardsCol>
                        {isHostBooking && (
                          <StyledLargestCol
                            md="12"
                            lg="3"
                            isHostBooking={isHostBooking}
                          >
                            <HostBookingDetailsContainer
                              errors={errors}
                              errorPopoverOpen={errorPopoverOpen}
                              closeErrorPopover={this.closeErrorPopover}
                              onNextClick={this.handleSearchClick.bind(
                                this,
                                selectedOffers
                              )}
                              isAdmin={isAdmin}
                              isHostBooking={isHostBooking}
                              inMobileApp={inMobileApp}
                            />
                          </StyledLargestCol>
                        )}
                      </Row>
                      {error && (
                        <ErrorModal
                          property={property}
                          isOpen={error}
                          error={error}
                        />
                      )}
                    </React.Fragment>
                  );
                }}
              </Query>
            );
          }}
        </ApolloConsumer>
      </React.Fragment>
    );
  }
}

DateSelection.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  property: PropTypes.string,
  roomType: PropTypes.object,
  setRoomType: PropTypes.func.isRequired,
  numberOfGuests: PropTypes.object,
  setNumberOfGuests: PropTypes.func,
  setQueryInput: PropTypes.func,
  queryInput: PropTypes.shape({
    accountNumber: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    propertyCode: PropTypes.string
  }),
  setAccountNumber: PropTypes.func,
  setAccountPin: PropTypes.func.isRequired,
  accountNumber: PropTypes.string,
  accountPin: PropTypes.string,
  submittedAccount: submittedAccountShape,
  submitRewardsAccount: PropTypes.func.isRequired,
  blockCode: PropTypes.string,
  submittedBlockCode: PropTypes.string,
  activeBlock: blockShape,
  setActiveBlock: PropTypes.func.isRequired,
  setSelectedOffers: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  setIsAdmin: PropTypes.func.isRequired,
  theme: PropTypes.object,
  bookingSource: PropTypes.string,
  setGuestProfiles: PropTypes.func.isRequired,
  secondAccountNumber: PropTypes.string,
  secondSubmittedAccount: submittedAccountShape,
  isWaitListBooking: PropTypes.bool,
  adminSelectedOffers: PropTypes.array,
  setAdminSelectedOffers: PropTypes.func,
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  setHotelOverrideCodes: PropTypes.func,
  hotelOverrideCodes: PropTypes.shape({
    id: PropTypes.number,
    hotelCode: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    lastModified: PropTypes.string
  }),
  maxStayExceeded: PropTypes.bool,
  setMaxStayExceeded: PropTypes.func,
  inMobileApp: PropTypes.bool
};

export default withTheme(DateSelection);
